import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import type { CreateMemberInput, Member, Package } from "@vereinfach/types";
import { api, apiUrl, capExceeded, errorMessage, mutate } from "../lib/api";
import { formatCHF, formatDate, monthsLabel, todayIso } from "../lib/format";
import { Modal } from "../components/Modal";

type Filter = "active" | "all";

type ImportResult = {
  created: number;
  created_ids: string[];
  failed: { row: number; error: string }[];
};

type BulkResult = {
  started: number;
  failed: { member_id: string; error: string }[];
};

export function MembersPage({ tenantId }: { tenantId: string }) {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("active");
  const [showArchived, setShowArchived] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [bulkIds, setBulkIds] = useState<string[] | null>(null);

  async function refresh() {
    try {
      const { members } = await api<{ members: Member[] }>(`/tenants/${tenantId}/members`);
      setMembers(members);
      setError(null);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const membersById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter((m) => {
      // Archived are hidden everywhere unless explicitly toggled on.
      if (m.status === "archived" && !showArchived) return false;
      if (filter === "active" && m.status !== "active") return false;
      if (!q) return true;
      const hay =
        `${m.first_name} ${m.last_name} ${m.org_name ?? ""} ${m.email ?? ""} ${m.city ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [members, search, filter, showArchived]);

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold tracking-tight">Mitglieder</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImport(true)}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium hover:bg-slate-50"
          >
            CSV-Import
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            Mitglied hinzufügen
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Suchen…"
          className="input max-w-xs"
        />
        <div className="flex gap-1 text-sm">
          {(
            [
              { key: "active", label: "Aktive" },
              { key: "all", label: "Alle" },
            ] as { key: Filter; label: string }[]
          ).map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={
                "rounded px-2.5 py-1 " +
                (filter === f.key
                  ? "bg-slate-900 text-white"
                  : "text-slate-500 hover:text-slate-900")
              }
            >
              {f.label}
            </button>
          ))}
        </div>
        <label className="ml-auto flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Archivierte anzeigen
        </label>
      </div>

      {loading ? (
        <p className="text-slate-400">Lädt…</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-slate-500">
          {members.length === 0 ? "Noch keine Mitglieder. Leg das erste an." : "Keine Treffer."}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">Name</th>
                <th className="px-4 py-2.5 text-left font-medium">E-Mail</th>
                <th className="px-4 py-2.5 text-left font-medium">Ort</th>
                <th className="px-4 py-2.5 text-left font-medium">Status</th>
                <th className="px-4 py-2.5 text-left font-medium">Eintritt</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5">
                    <Link
                      to="/members/$memberId"
                      params={{ memberId: m.id }}
                      className="font-medium text-slate-900 hover:underline"
                    >
                      {m.last_name}, {m.first_name}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 text-slate-500">{m.email ?? "—"}</td>
                  <td className="px-4 py-2.5 text-slate-500">{m.city ?? "—"}</td>
                  <td className="px-4 py-2.5">
                    <MemberStatusChip status={m.status} />
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-slate-500">
                    {formatDate(m.joined_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateMemberModal
          tenantId={tenantId}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            refresh();
          }}
        />
      )}

      {showImport && (
        <ImportMembersDialog
          tenantId={tenantId}
          onClose={() => setShowImport(false)}
          onImported={() => refresh()}
          onAssign={(ids) => {
            setShowImport(false);
            setBulkIds(ids);
          }}
        />
      )}

      {bulkIds && (
        <BulkAssignModal
          tenantId={tenantId}
          memberIds={bulkIds}
          memberName={(id) => {
            const m = membersById.get(id);
            return m ? `${m.first_name} ${m.last_name}` : id;
          }}
          onClose={() => setBulkIds(null)}
          onDone={() => {
            setBulkIds(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function MemberStatusChip({ status }: { status: Member["status"] }) {
  if (status === "active") {
    return (
      <span className="inline-block rounded px-2 py-0.5 text-xs bg-emerald-50 text-emerald-700">
        Aktiv
      </span>
    );
  }
  if (status === "archived") {
    return (
      <span className="inline-block rounded px-2 py-0.5 text-xs bg-slate-100 text-slate-400">
        Archiviert
      </span>
    );
  }
  return (
    <span className="inline-block rounded px-2 py-0.5 text-xs bg-slate-100 text-slate-500">
      Ausgetreten
    </span>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function CreateMemberModal({
  tenantId,
  onClose,
  onCreated,
}: {
  tenantId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState<CreateMemberInput>({
    first_name: "",
    last_name: "",
    country: "CH",
    joined_at: todayIso(),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capBlocked, setCapBlocked] = useState(false);

  function set<K extends keyof CreateMemberInput>(k: K, v: CreateMemberInput[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setCapBlocked(false);
    try {
      await mutate(`/tenants/${tenantId}/members`, { ...form });
      onCreated();
    } catch (err) {
      if (capExceeded(err)) {
        setCapBlocked(true);
      } else {
        setError(errorMessage(err));
      }
      setBusy(false);
    }
  }

  return (
    <Modal title="Mitglied hinzufügen" onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Vorname">
            <input
              required
              autoFocus
              value={form.first_name}
              onChange={(e) => set("first_name", e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Nachname">
            <input
              required
              value={form.last_name}
              onChange={(e) => set("last_name", e.target.value)}
              className="input"
            />
          </Field>
        </div>
        <Field label="Organisation / juristischer Name (optional)">
          <input
            value={form.org_name ?? ""}
            onChange={(e) => set("org_name", e.target.value || null)}
            className="input"
          />
        </Field>
        <Field label="E-Mail">
          <input
            type="email"
            value={form.email ?? ""}
            onChange={(e) => set("email", e.target.value || null)}
            className="input"
          />
        </Field>
        <Field label="Telefon">
          <input
            value={form.phone ?? ""}
            onChange={(e) => set("phone", e.target.value || null)}
            className="input"
          />
        </Field>
        <Field label="Strasse">
          <input
            value={form.address_line1 ?? ""}
            onChange={(e) => set("address_line1", e.target.value || null)}
            className="input"
          />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="PLZ">
            <input
              value={form.zip ?? ""}
              onChange={(e) => set("zip", e.target.value || null)}
              className="input"
            />
          </Field>
          <div className="col-span-2">
            <Field label="Ort">
              <input
                value={form.city ?? ""}
                onChange={(e) => set("city", e.target.value || null)}
                className="input"
              />
            </Field>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Geburtsdatum">
            <input
              type="date"
              value={form.date_of_birth ?? ""}
              onChange={(e) => set("date_of_birth", e.target.value || null)}
              className="input"
            />
          </Field>
          <Field label="Eintritt">
            <input
              type="date"
              value={form.joined_at ?? ""}
              onChange={(e) => set("joined_at", e.target.value || undefined)}
              className="input"
            />
          </Field>
        </div>
        <Field label="Notizen">
          <textarea
            rows={2}
            value={form.notes ?? ""}
            onChange={(e) => set("notes", e.target.value || null)}
            className="input"
          />
        </Field>

        {capBlocked && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Mitglieder-Limit erreicht.{" "}
            <Link to="/settings/abo" className="font-medium underline hover:no-underline">
              Plan upgraden
            </Link>
            , um weitere Mitglieder anzulegen.
          </div>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={busy}
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {busy ? "Speichern…" : "Mitglied anlegen"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ImportMembersDialog({
  tenantId,
  onClose,
  onImported,
  onAssign,
}: {
  tenantId: string;
  onClose: () => void;
  onImported: () => void;
  onAssign: (memberIds: string[]) => void;
}) {
  const [csvText, setCsvText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  async function submit() {
    if (!csvText.trim()) return;
    setBusy(true);
    setError(null);
    try {
      // Raw CSV body, not JSON — mutate()/api() would wrap it.
      const res = await fetch(apiUrl(`/tenants/${tenantId}/members/import`), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "text/csv; charset=utf-8" },
        body: csvText,
      });
      const data = (await res.json().catch(() => null)) as
        | (Partial<ImportResult> & { error?: string })
        | null;
      if (data && typeof data.created === "number") {
        setResult({
          created: data.created,
          created_ids: data.created_ids ?? [],
          failed: data.failed ?? [],
        });
        if (data.created > 0) onImported();
      } else if (data?.error === "member_cap_exceeded") {
        setError("Mitglieder-Limit erreicht — Plan upgraden.");
      } else {
        setError(data?.error ?? `HTTP ${res.status}`);
      }
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Mitglieder importieren" onClose={onClose}>
      {!result ? (
        <>
          <p className="text-sm text-slate-500">
            Lade die CSV-Vorlage herunter, fülle sie mit deinen Mitgliedern und lade sie
            anschliessend wieder hoch.
          </p>
          <a
            href={apiUrl(`/tenants/${tenantId}/members/import/template`)}
            className="mt-3 inline-block text-sm text-slate-900 underline hover:no-underline"
          >
            CSV-Vorlage herunterladen
          </a>

          <div className="mt-4">
            <input
              type="file"
              accept=".csv,text/csv"
              disabled={busy}
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (f) setCsvText(await f.text());
              }}
              className="block w-full text-sm"
            />
          </div>
          <Field label="…oder CSV-Inhalt einfügen">
            <textarea
              rows={5}
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              className="input font-mono text-xs"
              placeholder="first_name,last_name,email,…"
            />
          </Field>

          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
            >
              Abbrechen
            </button>
            <button
              type="button"
              disabled={busy || !csvText.trim()}
              onClick={submit}
              className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {busy ? "Importiere…" : "Importieren"}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-emerald-700">
            {result.created} Mitglied{result.created === 1 ? "" : "er"} importiert.
          </p>
          {result.failed.length > 0 && (
            <div className="mt-2 text-sm">
              <p className="text-red-600">
                {result.failed.length} Zeile{result.failed.length === 1 ? "" : "n"} übersprungen:
              </p>
              <ul className="mt-1 max-h-40 list-disc overflow-y-auto pl-5 text-xs text-slate-600">
                {result.failed.map((f, i) => (
                  <li key={i}>
                    Zeile {f.row}: {f.error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.created_ids.length > 0 && (
            <div className="mt-4 rounded-lg bg-slate-50 px-3 py-3 text-sm text-slate-700">
              <p className="font-medium">
                {result.created_ids.length} Mitglieder importiert — jetzt allen einen Beitrag
                zuteilen?
              </p>
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm"
                >
                  Später
                </button>
                <button
                  type="button"
                  onClick={() => onAssign(result.created_ids)}
                  className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white hover:bg-slate-800"
                >
                  Beitrag zuteilen
                </button>
              </div>
            </div>
          )}

          {result.created_ids.length === 0 && (
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
              >
                Schliessen
              </button>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

const BULK_ERROR_LABELS: Record<string, string> = {
  duplicate_subscription: "hat diesen Beitrag bereits",
  member_not_found: "Mitglied nicht gefunden",
  package_not_found: "Beitrag nicht gefunden",
};

function BulkAssignModal({
  tenantId,
  memberIds,
  memberName,
  onClose,
  onDone,
}: {
  tenantId: string;
  memberIds: string[];
  memberName: (id: string) => string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [packages, setPackages] = useState<Package[] | null>(null);
  const [packageId, setPackageId] = useState("");
  const [startDate, setStartDate] = useState(todayIso());
  const [autoRenew, setAutoRenew] = useState(true);
  const [paidUntil, setPaidUntil] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkResult | null>(null);

  useEffect(() => {
    api<{ packages: Package[] }>(`/tenants/${tenantId}/packages`)
      .then(({ packages }) => {
        const active = packages.filter((p) => !p.is_archived);
        setPackages(active);
        if (active[0]) setPackageId(active[0].id);
      })
      .catch((e) => setError(errorMessage(e)));
  }, [tenantId]);

  const pastWarning = startDate !== "" && startDate < todayIso() && !paidUntil;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        member_ids: memberIds,
        package_id: packageId,
        start_date: startDate,
        auto_renew: autoRenew,
      };
      if (paidUntil) body.paid_until = paidUntil;
      const res = await mutate<BulkResult>(`/tenants/${tenantId}/subscriptions/bulk`, body);
      setResult(res);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Beitrag zuteilen" onClose={result ? onDone : onClose}>
      {result ? (
        <>
          <p className="text-sm text-emerald-700">
            {result.started} Beitr{result.started === 1 ? "ag" : "äge"} gestartet.
          </p>
          {result.failed.length > 0 && (
            <div className="mt-2 text-sm">
              <p className="text-red-600">{result.failed.length} fehlgeschlagen:</p>
              <ul className="mt-1 max-h-40 list-disc overflow-y-auto pl-5 text-xs text-slate-600">
                {result.failed.map((f, i) => (
                  <li key={i}>
                    {memberName(f.member_id)}: {BULK_ERROR_LABELS[f.error] ?? f.error}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={onDone}
              className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white hover:bg-slate-800"
            >
              Fertig
            </button>
          </div>
        </>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <p className="text-sm text-slate-500">
            {memberIds.length} Mitglieder erhalten denselben Beitrag.
          </p>
          {packages === null ? (
            <p className="text-sm text-slate-400">Lädt Beiträge…</p>
          ) : packages.length === 0 ? (
            <p className="text-sm text-slate-500">
              Noch keine Beiträge angelegt. Lege zuerst unter «Beiträge» einen an.
            </p>
          ) : (
            <>
              <Field label="Beitrag">
                <select
                  value={packageId}
                  onChange={(e) => setPackageId(e.target.value)}
                  className="input"
                >
                  {packages.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} · {formatCHF(p.price_rappen)} / {monthsLabel(p.duration_months)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Startdatum">
                <input
                  type="date"
                  required
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="input"
                />
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={autoRenew}
                  onChange={(e) => setAutoRenew(e.target.checked)}
                />
                verlängert sich automatisch
              </label>
              <Field label="Bereits bezahlt bis (optional)">
                <input
                  type="date"
                  value={paidUntil}
                  onChange={(e) => setPaidUntil(e.target.value)}
                  className="input"
                />
              </Field>
              <p className="text-xs text-slate-500">
                Perioden vor diesem Datum werden übersprungen («vor Vereinfach bezahlt») — die erste
                Rechnung ist die nächste fällige.
              </p>
              {pastWarning && (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Achtung: erstellt Rechnungsentwürfe für vergangene Perioden — falls schon bezahlt,
                  ‹bereits bezahlt bis› setzen.
                </p>
              )}
            </>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={busy || !packageId || !startDate}
              className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {busy ? "Zuteilen…" : "Zuteilen"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
